import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <AnimatePresence>
      {project && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
          >
            <div className="relative glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8">
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-5 h-5 border-t border-l border-sky-400/50" />
              <div className="absolute top-0 right-0 w-5 h-5 border-t border-r border-sky-400/50" />
              <div className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-sky-400/50" />
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-sky-400/50" />

              {/* Close */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-sky-400 transition-colors p-1"
              >
                <FaTimes />
              </button>

              {/* Header */}
              <div className="flex items-center gap-3 mb-5">
                <span className="text-sky-400 text-xl">{project.icon}</span>
                <div>
                  <span className="font-mono text-[10px] text-gray-500 block">{"// project.details"}</span>
                  <h3 className="font-mono text-lg font-bold text-white">{project.name}</h3>
                </div>
              </div>

              {/* Description */}
              <div className="mb-5">
                <span className="font-mono text-[10px] text-sky-400/70 block mb-2">{"// overview"}</span>
                <p className="text-gray-300 text-sm leading-relaxed">{project.description}</p>
              </div>

              {/* Key impact */}
              <div className="mb-5">
                <span className="font-mono text-[10px] text-sky-400/70 block mb-3">{"// key_impact"}</span>
                <div className="space-y-2">
                  {project.impact.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="font-mono text-sky-400 text-xs mt-0.5 shrink-0">&#9656;</span>
                      <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tech stack */}
              <div className="mb-5">
                <span className="font-mono text-[10px] text-sky-400/70 block mb-3">{"// tech_stack"}</span>
                <div className="flex flex-wrap gap-2">
                  {project.techstack.map((tech) => (
                    <span key={tech} className="tech-badge">{tech}</span>
                  ))}
                </div>
              </div>

              {/* Note */}
              {project.note && (
                <p className="font-mono text-[10px] text-gray-500 italic border-t border-sky-400/10 pt-4 mt-2">
                  * {project.note} — demo available upon request
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
